import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  EnumCutsheetQuestionnaireQuestionsType,
  useCutsheetQuestionnaireResponseUpsertMutation,
} from 'lib/graphql';
import type {
  ChoiceGroupQuestion,
  CutsheetCatalog,
  CutsheetQuestionnaire,
  FreeSelectionQuestion,
  GlobalSpecQuestion,
  Question,
  TrimPercentageQuestion,
  TrimRankingQuestion,
} from '../../types';
import { format } from 'date-fns';
import type {
  CutsheetResponseDraft,
  InitialResponseSeed,
  ItemSpecValues,
  ResponseCutOption,
  ResponsePercentageTrimSelection,
  ResponseRankedTrimSelection,
  ResponseSpecificationSelection,
  ResponseTrimSelections,
  TrimPercentageSelection,
  TrimRankingSelection,
} from './types';
import { getAvailableSaveTypes } from './wizard-utils';

/**
 * Union of all allowed values for a single questionnaire question answer.
 * Simple questions store a string or string[]; TrimRanking/TrimPercentage
 * questions store their respective selection objects.
 */
export type QuestionAnswer =
  | string
  | string[]
  | TrimRankingSelection[]
  | TrimPercentageSelection[];

// ─── Context shape ────────────────────────────────────────────────────────────

interface QuestionnaireResponseContextValue {
  // ── Source data (read-only) ─────────────────────────────────────────────────
  questionnaire: CutsheetQuestionnaire;
  catalog: CutsheetCatalog;

  // ── Navigation ──────────────────────────────────────────────────────────────
  currentStep: number;
  isFirstStep: boolean;
  isLastQuestionStep: boolean;
  isReviewStep: boolean;
  isSaveStep: boolean;
  hasAdvancedStep: boolean;
  hasVisitedReview: boolean;
  heroExpanded: boolean;
  setHeroExpanded: (expanded: boolean | ((curr: boolean) => boolean)) => void;
  currentQuestion: Question | undefined;
  /** Set of cut IDs referenced in any questionnaire question (null = no filter) */
  questionnaireCutIds: Set<string> | null;
  handleNext: () => void;
  handleBack: () => void;
  handleGoToReview: () => void;
  handleGoToSave: () => void;
  handleNavigateToQuestion: (index: number) => void;
  handleClose: () => void;

  // ── Wizard UI state ─────────────────────────────────────────────────────────
  // Read by wizard step components for display only.  Every mutation below also
  // writes through to the canonical response state.
  answers: Record<string, QuestionAnswer>;
  itemSpecValues: ItemSpecValues;
  subSelections: Record<string, string[]>;
  handleAnswerChange: (questionId: string, value: QuestionAnswer) => void;
  handleSpecChange: (
    questionId: string,
    itemId: string,
    specId: string,
    value: string,
  ) => void;
  handleSubSelectionToggle: (questionId: string, cutId: string) => void;
  /** Resets all sub-selections for a choice-group question (used when the main option changes) */
  handleClearSubSelections: (questionId: string) => void;

  // ── Canonical response state ────────────────────────────────────────────────
  // Mirrors CutsheetQuestionnaireResponseModel.  Both wizard steps and the
  // advanced view write here directly, so there is one source of truth.
  /**
   * Cut selections — shape matches `CutsheetQuestionnaireResponseModel.selectedCuts`.
   * Written by FreeSelection/ChoiceGroup answers, per-cut spec changes, and
   * direct toggling in the advanced view.
   */
  selectedCuts: ResponseCutOption[];
  /**
   * Global specification answers — shape matches
   * `CutsheetQuestionnaireResponseModel.specifications`.
   * Written by GlobalSpec question answers and the advanced-view spec sidebar.
   */
  specifications: ResponseSpecificationSelection[];
  /**
   * Trim selections — shape matches
   * `CutsheetQuestionnaireResponseModel.trimSelections`.
   * Written by TrimRanking/TrimPercentage answers and the advanced-view trim panel.
   */
  trimSelections: ResponseTrimSelections | undefined;
  primalNotes: Record<string, string>;
  trimSectionNotes: string;

  // ── Derived convenience values ──────────────────────────────────────────────
  // Flat look-up maps computed from the canonical state above.  Consumers that
  // already use these don't need to change.
  /** Set of selected cut IDs, derived from `selectedCuts`. */
  selectedCutIds: Set<string>;
  /** Flat `cutId → { specId → value }` map, derived from `selectedCuts`. */
  cutSpecValues: Record<string, Record<string, string>>;
  /** Flat `specId → value` map, derived from `specifications`. Multiselect values are preserved as arrays. */
  advancedSpecValues: Record<string, string | string[]>;
  /** Ordered trim IDs, derived from `trimSelections`. */
  selectedTrimIds: string[];

  handleToggleCut: (cutId: string) => void;
  handlePrimalNoteChange: (primalId: string, note: string) => void;
  handleAdvancedSpecValueChange: (
    specId: string,
    value: string | string[],
  ) => void;
  handleCutSpecValueChange: (
    cutId: string,
    specId: string,
    value: string,
  ) => void;
  /**
   * Whether trim selections should be treated as ranked (weight in lbs) or
   * percentage-based. Derived from the questionnaire's trim question type; if
   * no trim question exists, defaults to 'ranked'.
   */
  trimMode: 'ranked' | 'selection_by_percentage';
  handleAddTrim: (trimId: string) => void;
  handleRemoveTrim: (index: number) => void;
  handleMoveTrim: (index: number, direction: 'up' | 'down') => void;
  handleTrimSectionNoteChange: (note: string) => void;
  handleTrimSpecValueChange: (
    trimSelectionIndex: number,
    specId: string,
    value: string,
  ) => void;
  handleTrimWeightChange: (index: number, weight: number) => void;
  handleTrimPercentageChange: (index: number, percentage: number) => void;

  // ── Validation ──────────────────────────────────────────────────────────────
  /** Per-question error messages. Empty string = valid. */
  questionErrors: Record<string, string>;
  /** Error message for the current question step (empty string if valid). */
  currentQuestionError: string;

  // ── Derived response model ──────────────────────────────────────────────────
  /** API-ready draft — direct pass-through of the canonical state above. */
  responseDraft: CutsheetResponseDraft;

  // ── Persistence ─────────────────────────────────────────────────────────────
  /** Whether the upsert mutation is in flight. */
  isSaving: boolean;
  /**
   * Persists the current response draft via the upsert mutation, then closes
   * the dialog.  Rejects if `cutsheetName` is empty.
   */
  handleSave: () => Promise<void>;

  // ── Save-step naming state ──────────────────────────────────────────────────
  /** Name the customer gives their cutsheet on the save step. */
  cutsheetName: string;
  /** How the cutsheet should be saved (vendor house, producer, buyer, or personal). */
  cutsheetSaveType: 'user' | 'vendor' | 'producer' | 'buyer' | '';
  /** Props threaded from QuestionnaireWizard for role-based save-type options. */
  isProcessor: boolean;
  producerId: string | undefined;
  buyerId: string | undefined;
  /** When set, the save-type radio group is hidden and this type is used. */
  fixedCutsheetType:
    | 'vendor'
    | 'producer'
    | 'buyer'
    | 'user'
    | 'orphan'
    | undefined;
  /** When set, this user ID is sent to the upsert mutation regardless of save type. */
  fixedUserId: string | undefined;
  handleCutsheetNameChange: (name: string) => void;
  handleCutsheetSaveTypeChange: (
    type: 'user' | 'vendor' | 'producer' | 'buyer',
  ) => void;
}

const QuestionnaireResponseContext =
  createContext<QuestionnaireResponseContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

interface QuestionnaireResponseProviderProps {
  questionnaire: CutsheetQuestionnaire;
  catalog: CutsheetCatalog;
  onClose: () => void;
  /** Called after a successful save with the persisted response ID. */
  onSave?: (responseId: string) => void;
  children: ReactNode;
  /** When true, extra processor-only save options (house, producer) are shown. */
  isProcessor?: boolean;
  producerId?: string;
  buyerId?: string;
  /** When provided, the save-type radio group is hidden and this type is used. */
  fixedCutsheetType?: 'vendor' | 'producer' | 'buyer' | 'user' | 'orphan';
  /**
   * When provided, this user ID is sent to the upsert mutation regardless
   * of save type — used to retain ownership when converting a legacy cutsheet.
   */
  fixedUserId?: string;
  /**
   * When provided, seeds the wizard state from this response instead of
   * the questionnaire's defaultSelections (used by the Edit flow).
   */
  initialResponse?: InitialResponseSeed;
}

export const QuestionnaireResponseProvider: FC<
  QuestionnaireResponseProviderProps
> = ({
  questionnaire,
  catalog,
  onClose,
  onSave,
  children,
  isProcessor = false,
  producerId = undefined,
  buyerId = undefined,
  fixedCutsheetType = undefined,
  fixedUserId = undefined,
  initialResponse = undefined,
}) => {
  // When editing an existing response, seed state from the response;
  // otherwise fall back to whatever defaults the questionnaire carries.
  const seed = initialResponse ?? questionnaire.defaultSelections;

  // ── Navigation state ────────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(0);
  const [hasVisitedReview, setHasVisitedReview] = useState(false);
  const [heroExpanded, setHeroExpanded] = useState(false);

  // ── Save-step naming state ────────────────────────────────────────────────
  const [cutsheetName, setCutsheetName] = useState(() =>
    initialResponse?.name
      ? `${initialResponse.name} (${format(new Date(), 'MMM d, yyyy')})`
      : '',
  );
  const [cutsheetSaveType, setCutsheetSaveType] = useState<
    'user' | 'vendor' | 'producer' | 'buyer' | ''
  >(() => {
    if (fixedCutsheetType) return '';
    const available = getAvailableSaveTypes({
      isProcessor,
      producerId,
      buyerId,
    });
    return available.length === 1 ? available[0] : '';
  });

  const handleCutsheetNameChange = useCallback(
    (name: string) => setCutsheetName(name),
    [],
  );
  const handleCutsheetSaveTypeChange = useCallback(
    (type: 'user' | 'vendor' | 'producer' | 'buyer') =>
      setCutsheetSaveType(type),
    [],
  );

  // ── Persistence ──────────────────────────────────────────────────────────────

  const [upsertResponse, { loading: isSaving }] =
    useCutsheetQuestionnaireResponseUpsertMutation();

  // ── Wizard UI state ─────────────────────────────────────────────────────────
  // Read by wizard step components for display only.  Every mutation below also
  // writes through to the canonical response state.

  /**
   * Back-derive initial wizard answer/spec/sub-selection state from the seed
   * (either `initialResponse` when editing, or `questionnaire.defaultSelections`)
   * so question steps show pre-filled values.
   */
  const initialWizardState = useMemo(() => {
    const sel = seed;
    const initAnswers: Record<string, QuestionAnswer> = {};
    const initItemSpecValues: ItemSpecValues = {};
    const initSubSelections: Record<string, string[]> = {};

    if (!sel) return { initAnswers, initItemSpecValues, initSubSelections };

    const selectedIds = new Set(sel.selectedCuts?.map((c) => c.optionId) ?? []);
    // cutId -> { specId -> value } from the pre-selected cuts
    const cutSpecMap: Record<string, Record<string, string>> = {};
    for (const cut of sel.selectedCuts ?? []) {
      if (cut.specifications?.length) {
        cutSpecMap[cut.optionId] = {};
        for (const spec of cut.specifications) {
          cutSpecMap[cut.optionId][spec.specification] = String(spec.value);
        }
      }
    }

    for (const question of questionnaire.questions) {
      switch (question.type) {
        case 'free_selection': {
          const fq = question as FreeSelectionQuestion;
          const selected = fq.availableCutIds.filter((id) =>
            selectedIds.has(id),
          );
          if (selected.length > 0) {
            initAnswers[question.id] =
              fq.allowMultiple === false ? selected[0] : selected;
            for (const cutId of selected) {
              if (cutSpecMap[cutId]) {
                initItemSpecValues[question.id] = {
                  ...(initItemSpecValues[question.id] ?? {}),
                  [cutId]: cutSpecMap[cutId],
                };
              }
            }
          }
          break;
        }
        case 'choice_group': {
          const cgq = question as ChoiceGroupQuestion;
          for (const option of cgq.options) {
            if (cgq.allowSubSelection) {
              const selectedFromOption = option.cutIds.filter((id) =>
                selectedIds.has(id),
              );
              if (selectedFromOption.length > 0) {
                initAnswers[question.id] = option.id;
                initSubSelections[question.id] = selectedFromOption;
                for (const cutId of selectedFromOption) {
                  if (cutSpecMap[cutId]) {
                    initItemSpecValues[question.id] = {
                      ...(initItemSpecValues[question.id] ?? {}),
                      [cutId]: cutSpecMap[cutId],
                    };
                  }
                }
                break;
              }
            } else {
              const allSelected =
                option.cutIds.length > 0 &&
                option.cutIds.every((id) => selectedIds.has(id));
              if (allSelected) {
                initAnswers[question.id] = option.id;
                break;
              }
            }
          }
          break;
        }
        case 'global_spec': {
          const gq = question as GlobalSpecQuestion;
          const specId = gq.specificationId ?? gq.id;
          const specAnswer = sel.specifications?.find(
            (s) => s.specification === specId,
          );
          if (specAnswer) {
            initAnswers[question.id] = specAnswer.value as string | string[];
          } else {
            // No explicit default in the questionnaire, but the spec itself
            // might carry a defaultValue. Seed the answer so the validator
            // (which reads canonical state) stays in sync with the UI (which
            // falls back to spec.defaultValue when no answer is set).
            const spec = catalog.specifications.find((s) => s.id === specId);
            if (spec?.defaultValue) {
              initAnswers[question.id] = spec.defaultValue;
            }
          }
          break;
        }
        case 'trim_ranking': {
          if (
            sel.trimSelections?.type === 'ranked' &&
            sel.trimSelections.trim.length > 0
          ) {
            initAnswers[question.id] = sel.trimSelections.trim.map((t, i) => ({
              id: String(i),
              trimId: t.optionId,
              rank: 'rank' in t ? (t.rank as number) : i + 1,
              weight: 'quantity' in t ? (t.quantity as number) : 0,
            })) as unknown as QuestionAnswer;
          }
          break;
        }
        case 'trim_percentage': {
          if (
            sel.trimSelections?.type === 'selection_by_percentage' &&
            sel.trimSelections.trim.length > 0
          ) {
            initAnswers[question.id] = sel.trimSelections.trim.map((t, i) => ({
              id: String(i),
              trimId: t.optionId,
              percentage: 'percentage' in t ? (t.percentage as number) : 0,
            })) as unknown as QuestionAnswer;
          }
          break;
        }
        default:
          break;
      }
    }

    return { initAnswers, initItemSpecValues, initSubSelections };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaire.id, seed]);

  const [answers, setAnswers] = useState<Record<string, QuestionAnswer>>(
    initialWizardState.initAnswers,
  );
  // Always-current ref so callbacks can read answers without stale closures.
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const [itemSpecValues, setItemSpecValues] = useState<ItemSpecValues>(
    initialWizardState.initItemSpecValues,
  );
  const [subSelections, setSubSelections] = useState<Record<string, string[]>>(
    initialWizardState.initSubSelections,
  );

  // ── Advanced view derived values ─────────────────────────────────────────────

  /**
   * True when the catalog has primals — enables the Advanced View step.
   * Memoised on questionnaire identity so it doesn't re-run on every render.
   */
  const hasAdvancedStep = catalog.primals.length > 0;

  /** Cuts referenced in any question (used to grey out unrelated cuts in the advanced view). */
  const questionnaireCutIds = useMemo((): Set<string> | null => {
    const ids = new Set<string>();
    for (const q of questionnaire.questions) {
      if (q.type === 'free_selection') {
        (q as FreeSelectionQuestion).availableCutIds.forEach((id) =>
          ids.add(id),
        );
      } else if (q.type === 'choice_group') {
        (q as ChoiceGroupQuestion).options.forEach((opt) =>
          opt.cutIds.forEach((id) => ids.add(id)),
        );
      }
    }
    return ids.size > 0 ? ids : null;
  }, [questionnaire.questions]);

  // ── Canonical response state ─────────────────────────────────────────────────
  // Mirrors CutsheetQuestionnaireResponseModel (cuts, specifications, trim).
  // Both wizard steps and the advanced view write here directly so there is
  // one source of truth that can be submitted as-is.

  /** Initial cut selections from the seed (used for reset). */
  const initialSelectedCuts = useMemo<ResponseCutOption[]>(
    () => seed?.selectedCuts ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questionnaire.id, seed],
  );

  /** Initial trim selections from the seed (used for reset). */
  const initialTrimSelections = useMemo<ResponseTrimSelections | undefined>(
    () => seed?.trimSelections,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questionnaire.id, seed],
  );

  /**
   * Initial specifications from the seed (used for reset).
   * For any global_spec question whose spec has a `defaultValue` but no
   * explicit answer in the seed, we seed an entry here so the canonical
   * state matches what the UI displays — otherwise required-field
   * validation would fire on a spec that looks selected but isn't in state.
   */
  const initialSpecifications = useMemo<
    ResponseSpecificationSelection[]
  >(() => {
    const existing = seed?.specifications ?? [];
    const seeded = [...existing];
    const already = new Set(seeded.map((s) => s.specification));

    for (const question of questionnaire.questions) {
      if (question.type !== 'global_spec') continue;
      const gq = question as GlobalSpecQuestion;
      const specId = gq.specificationId;
      if (!specId || already.has(specId)) continue;

      const spec = catalog.specifications.find((s) => s.id === specId);
      if (!spec?.defaultValue) continue;

      seeded.push({
        specification: specId,
        type: spec.inputType,
        value: spec.defaultValue,
      });
      already.add(specId);
    }

    return seeded;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaire.id, seed]);

  /** Initial primal notes from the seed (used for reset). */
  const initialPrimalNotes = useMemo<Record<string, string>>(
    () => seed?.primalNotes ?? {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questionnaire.id, seed],
  );

  const [selectedCuts, setSelectedCuts] =
    useState<ResponseCutOption[]>(initialSelectedCuts);
  const [specifications, setSpecifications] = useState<
    ResponseSpecificationSelection[]
  >(initialSpecifications);
  const [trimSelections, setTrimSelections] = useState<
    ResponseTrimSelections | undefined
  >(initialTrimSelections);
  const [primalNotes, setPrimalNotes] =
    useState<Record<string, string>>(initialPrimalNotes);
  const [trimSectionNotes, setTrimSectionNotes] = useState<string>('');

  // ── Derived convenience values ───────────────────────────────────────────────
  // Flat look-up maps computed from the canonical state.  Consumers that already
  // use these don't need to change.

  /** Set of selected cut IDs, derived from `selectedCuts`. */
  const selectedCutIds = useMemo(
    () => new Set(selectedCuts.map((c) => c.optionId)),
    [selectedCuts],
  );

  /** Flat `cutId → { specId → value }` map, derived from `selectedCuts`. */
  const cutSpecValues = useMemo<Record<string, Record<string, string>>>(() => {
    const map: Record<string, Record<string, string>> = {};
    for (const cut of selectedCuts) {
      if (!cut.specifications?.length) continue;
      map[cut.optionId] = {};
      for (const spec of cut.specifications) {
        map[cut.optionId][spec.specification] = String(spec.value);
      }
    }
    return map;
  }, [selectedCuts]);

  /** Flat `specId → value` map, derived from `specifications`. */
  const advancedSpecValues = useMemo<Record<string, string | string[]>>(() => {
    const map: Record<string, string | string[]> = {};
    for (const spec of specifications) {
      map[spec.specification] = Array.isArray(spec.value)
        ? spec.value
        : String(spec.value);
    }
    return map;
  }, [specifications]);

  /** Ordered trim IDs, derived from `trimSelections`. */
  const selectedTrimIds = useMemo(
    () => trimSelections?.trim.map((t) => t.optionId) ?? [],
    [trimSelections],
  );

  // ── Validation ───────────────────────────────────────────────────────────────

  const questionErrors = useMemo<Record<string, string>>(() => {
    const errors: Record<string, string> = {};

    for (const question of questionnaire.questions) {
      switch (question.type) {
        case EnumCutsheetQuestionnaireQuestionsType.FreeSelection: {
          const fq = question as FreeSelectionQuestion;
          const pool = new Set(fq.availableCutIds);
          const count = selectedCuts.filter((c) => pool.has(c.optionId)).length;
          // required with no explicit minSelections means at least 1
          const effectiveMin = fq.minSelections ?? (question.required ? 1 : 0);

          if (count < effectiveMin) {
            errors[question.id] =
              `Please select at least ${effectiveMin} option${effectiveMin === 1 ? '' : 's'}.`;
          } else if (fq.maxSelections && count > fq.maxSelections) {
            errors[question.id] =
              `Please select no more than ${fq.maxSelections} option${fq.maxSelections === 1 ? '' : 's'}.`;
          }
          break;
        }

        case EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup: {
          const cgq = question as ChoiceGroupQuestion;
          const allOptionCutIds = new Set(cgq.options.flatMap((o) => o.cutIds));
          const hasSelection = selectedCuts.some((c) =>
            allOptionCutIds.has(c.optionId),
          );
          if (question.required && !hasSelection) {
            errors[question.id] = 'Please select an option.';
          }
          break;
        }

        case EnumCutsheetQuestionnaireQuestionsType.GlobalSpec: {
          const gq = question as GlobalSpecQuestion;
          const specId = gq.specificationId ?? gq.id;
          const entry = specifications.find((s) => s.specification === specId);
          const isEmpty =
            !entry ||
            entry.value === '' ||
            (Array.isArray(entry.value) && entry.value.length === 0);
          if (question.required && isEmpty) {
            errors[question.id] = 'This field is required.';
          }
          break;
        }

        case EnumCutsheetQuestionnaireQuestionsType.TrimRanking: {
          const trq = question as TrimRankingQuestion;
          const count =
            trimSelections?.type === 'ranked' ? trimSelections.trim.length : 0;
          if (question.required && count === 0) {
            errors[question.id] = 'Please select at least one trim option.';
          } else if (trq.maxSelections && count > trq.maxSelections) {
            errors[question.id] =
              `Please select no more than ${trq.maxSelections} trim option${trq.maxSelections === 1 ? '' : 's'}.`;
          }
          break;
        }

        case EnumCutsheetQuestionnaireQuestionsType.TrimPercentage: {
          const tpq = question as TrimPercentageQuestion;
          const trims =
            trimSelections?.type === 'selection_by_percentage'
              ? trimSelections.trim
              : [];
          if (question.required && trims.length === 0) {
            errors[question.id] = 'Please select at least one trim option.';
          } else if (tpq.requireExactTotal && trims.length > 0) {
            const total = trims.reduce(
              (sum, t) => sum + ('percentage' in t ? t.percentage : 0),
              0,
            );
            if (Math.abs(total - 100) > 0.01) {
              errors[question.id] =
                `Percentages must total 100% (currently ${Math.round(total)}%).`;
            }
          }
          break;
        }

        default:
          break;
      }
    }

    return errors;
  }, [questionnaire.questions, selectedCuts, specifications, trimSelections]);

  // ── Navigation handlers ─────────────────────────────────────────────────────
  // No seeding step needed — canonical state is always up to date.

  const handleNext = useCallback(() => {
    setCurrentStep((prev) => {
      const next = prev + 1;
      if (next === questionnaire.questions.length) setHasVisitedReview(true);
      return next;
    });
    setHeroExpanded(false);
  }, [questionnaire.questions.length]);

  const handleBack = useCallback(() => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
    setHeroExpanded(false);
  }, []);

  const handleGoToReview = useCallback(() => {
    setCurrentStep(questionnaire.questions.length);
    setHasVisitedReview(true);
  }, [questionnaire.questions.length]);

  const handleGoToSave = useCallback(() => {
    setCurrentStep(questionnaire.questions.length + 1);
    setHasVisitedReview(true);
  }, [questionnaire.questions.length]);

  const handleNavigateToQuestion = useCallback((index: number) => {
    setCurrentStep(index);
    setHeroExpanded(false);
  }, []);

  const handleClose = useCallback(() => {
    setCurrentStep(0);
    setHasVisitedReview(false);
    setAnswers(initialWizardState.initAnswers);
    setItemSpecValues(initialWizardState.initItemSpecValues);
    setSubSelections(initialWizardState.initSubSelections);
    setSelectedCuts(initialSelectedCuts);
    setSpecifications(initialSpecifications);
    setTrimSelections(initialTrimSelections);
    setPrimalNotes(initialPrimalNotes);
    setTrimSectionNotes('');
    setHeroExpanded(false);
    onClose();
  }, [
    initialSelectedCuts,
    initialTrimSelections,
    initialSpecifications,
    initialPrimalNotes,
    initialWizardState,
    onClose,
  ]);

  // ── Wizard handlers ─────────────────────────────────────────────────────────
  // Each handler keeps wizard UI state and canonical response state in sync so
  // both halves of the form always read and write the same data.

  /**
   * Called by every wizard step when the user changes their answer.
   * Updates wizard UI state AND the relevant canonical field immediately.
   */
  const handleAnswerChange = useCallback(
    (questionId: string, value: QuestionAnswer) => {
      // ── Wizard UI ──────────────────────────────────────────────────────────
      setAnswers((prev) => ({ ...prev, [questionId]: value }));

      // ── Canonical state ────────────────────────────────────────────────────
      const question = questionnaire.questions.find((q) => q.id === questionId);
      if (!question) return;

      switch (question.type) {
        case EnumCutsheetQuestionnaireQuestionsType.FreeSelection: {
          // Replace the cuts from this question's pool with the new selection.
          const fq = question as FreeSelectionQuestion;
          const selected = new Set(
            Array.isArray(value)
              ? (value as string[])
              : value
                ? [value as string]
                : [],
          );
          setSelectedCuts((prev) => {
            const withoutPool = prev.filter(
              (c) => !fq.availableCutIds.includes(c.optionId),
            );
            const added = [...selected].map((cutId) => ({
              optionId: cutId,
              specifications: prev.find((c) => c.optionId === cutId)
                ?.specifications,
            }));
            return [...withoutPool, ...added];
          });
          break;
        }

        case EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup: {
          // Replace cuts from any option of this question with the new selection.
          const cgq = question as ChoiceGroupQuestion;
          const allOptionCutIds = new Set(cgq.options.flatMap((o) => o.cutIds));
          const chosenOption = cgq.options.find(
            (o) => o.id === (value as string),
          );
          // Clear sub-selection UI state — the new option starts fresh.
          setSubSelections((prev) => ({ ...prev, [questionId]: [] }));
          setSelectedCuts((prev) => {
            const withoutPool = prev.filter(
              (c) => !allOptionCutIds.has(c.optionId),
            );
            if (!chosenOption || cgq.allowSubSelection) {
              // Cuts will be added one-by-one via handleSubSelectionToggle.
              return withoutPool;
            }
            const added = chosenOption.cutIds.map((cutId) => ({
              optionId: cutId,
              specifications: prev.find((c) => c.optionId === cutId)
                ?.specifications,
            }));
            return [...withoutPool, ...added];
          });
          break;
        }

        case 'global_spec': {
          // Upsert or remove the global specification answer.
          const gq = question as GlobalSpecQuestion;
          const specId = gq.specificationId ?? gq.id;
          const specDef = catalog.specifications.find((s) => s.id === specId);
          const isEmpty =
            value === undefined ||
            value === '' ||
            (Array.isArray(value) && value.length === 0);
          setSpecifications((prev) => {
            if (isEmpty) return prev.filter((s) => s.specification !== specId);
            const entry: ResponseSpecificationSelection = {
              specification: specId,
              type: specDef?.inputType ?? 'text',
              value: value as string | number | string[],
            };
            const idx = prev.findIndex((s) => s.specification === specId);
            if (idx === -1) return [...prev, entry];
            return prev.map((s, i) => (i === idx ? entry : s));
          });
          break;
        }

        case EnumCutsheetQuestionnaireQuestionsType.TrimRanking: {
          if (
            Array.isArray(value) &&
            value.length > 0 &&
            typeof value[0] === 'object'
          ) {
            const ranked = value as unknown as TrimRankingSelection[];
            setTrimSelections((prevTrim) => ({
              type: 'ranked',
              trim: ranked.map((r, i) => {
                // Use index to preserve per-trim specs across rebuilds.
                // find() would copy the first duplicate's specs to all entries
                // sharing the same optionId when the same trim is added twice.
                const existing = prevTrim?.trim[i];
                const specsToKeep =
                  existing?.optionId === r.trimId
                    ? existing.specifications
                    : prevTrim?.trim.find((t) => t.optionId === r.trimId)
                        ?.specifications;
                return {
                  optionId: r.trimId,
                  rank: r.rank,
                  quantity: r.weight ?? 0,
                  ...(specsToKeep ? { specifications: specsToKeep } : {}),
                };
              }),
            }));
          } else {
            setTrimSelections(initialTrimSelections);
          }
          break;
        }

        case EnumCutsheetQuestionnaireQuestionsType.TrimPercentage: {
          if (
            Array.isArray(value) &&
            value.length > 0 &&
            typeof value[0] === 'object'
          ) {
            const percentages = value as unknown as TrimPercentageSelection[];
            setTrimSelections((prevTrim) => ({
              type: 'selection_by_percentage',
              trim: percentages.map((p, i) => {
                // Index-first lookup so duplicate trims (same optionId) keep
                // their specs independent across re-orderings/removals.
                const existing = prevTrim?.trim[i];
                const specsToKeep =
                  existing?.optionId === p.trimId
                    ? existing.specifications
                    : prevTrim?.trim.find((t) => t.optionId === p.trimId)
                        ?.specifications;
                return {
                  optionId: p.trimId,
                  percentage: p.percentage,
                  ...(specsToKeep ? { specifications: specsToKeep } : {}),
                };
              }),
            }));
          } else {
            setTrimSelections(initialTrimSelections);
          }
          break;
        }

        default:
          break;
      }
    },
    [catalog.specifications, initialTrimSelections, questionnaire.questions],
  );

  /**
   * Called when the user sets a per-item spec value inside a wizard step.
   * Updates wizard UI state AND the canonical state for cuts or trim selections.
   */
  const handleSpecChange = useCallback(
    (questionId: string, itemId: string, specId: string, value: string) => {
      // Wizard UI state
      setItemSpecValues((prev) => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          [itemId]: { ...(prev[questionId]?.[itemId] || {}), [specId]: value },
        },
      }));

      // Canonical state — for trim questions, update trimSelections; otherwise update selectedCuts.
      const question = questionnaire.questions.find((q) => q.id === questionId);
      if (
        question?.type === EnumCutsheetQuestionnaireQuestionsType.TrimRanking ||
        question?.type === EnumCutsheetQuestionnaireQuestionsType.TrimPercentage
      ) {
        // itemId is the selection UUID — find the corresponding index via current answers
        // so duplicate trims (same optionId) are updated independently.
        const trimAnswer = answersRef.current[questionId] as
          | TrimRankingSelection[]
          | TrimPercentageSelection[];
        const selectionIdx = trimAnswer?.findIndex((s) => s.id === itemId);
        if (selectionIdx == null || selectionIdx === -1) return;
        setTrimSelections((prev) => {
          if (!prev || !prev.trim[selectionIdx]) return prev;
          return {
            ...prev,
            trim: prev.trim.map((t, i) => {
              if (i !== selectionIdx) return t;
              const existingSpecs = t.specifications ?? [];
              const specIdx = existingSpecs.findIndex(
                (s) => s.specification === specId,
              );
              let newSpecs: ResponseSpecificationSelection[];
              if (!value) {
                newSpecs = existingSpecs.filter(
                  (s) => s.specification !== specId,
                );
              } else if (specIdx === -1) {
                newSpecs = [
                  ...existingSpecs,
                  { specification: specId, type: 'text' as const, value },
                ];
              } else {
                newSpecs = existingSpecs.map((s, i) =>
                  i === specIdx ? { ...s, value } : s,
                );
              }
              return {
                ...t,
                specifications: newSpecs.length > 0 ? newSpecs : undefined,
              };
            }),
          };
        });
        return;
      }

      // Canonical state — update specs on the matching cut
      setSelectedCuts((prev) => {
        const idx = prev.findIndex((c) => c.optionId === itemId);
        if (idx === -1) return prev; // cut not selected; ignore
        const cut = prev[idx];
        const existingSpecs = cut.specifications ?? [];
        const specIdx = existingSpecs.findIndex(
          (s) => s.specification === specId,
        );
        let newSpecs: ResponseSpecificationSelection[];
        if (!value) {
          newSpecs = existingSpecs.filter((s) => s.specification !== specId);
        } else if (specIdx === -1) {
          newSpecs = [
            ...existingSpecs,
            { specification: specId, type: 'text' as const, value },
          ];
        } else {
          newSpecs = existingSpecs.map((s, i) =>
            i === specIdx ? { ...s, value } : s,
          );
        }
        const updated: ResponseCutOption = {
          ...cut,
          specifications: newSpecs.length > 0 ? newSpecs : undefined,
        };
        return prev.map((c, i) => (i === idx ? updated : c));
      });
    },
    [questionnaire.questions],
  );

  /**
   * Toggles an individual cut within a ChoiceGroup sub-selection.
   * Updates wizard UI state AND `selectedCuts`.
   */
  const handleSubSelectionToggle = useCallback(
    (questionId: string, cutId: string) => {
      // Wizard UI state
      setSubSelections((prev) => {
        const current = prev[questionId] || [];
        const updated = current.includes(cutId)
          ? current.filter((id) => id !== cutId)
          : [...current, cutId];
        return { ...prev, [questionId]: updated };
      });
      // Canonical state — toggle the cut in selectedCuts
      setSelectedCuts((prev) => {
        const exists = prev.some((c) => c.optionId === cutId);
        if (exists) return prev.filter((c) => c.optionId !== cutId);
        return [...prev, { optionId: cutId }];
      });
    },
    [],
  );

  // ── Advanced view handlers ──────────────────────────────────────────────────
  // These write directly to canonical state.

  const handleToggleCut = useCallback((cutId: string) => {
    setSelectedCuts((prev) => {
      const exists = prev.some((c) => c.optionId === cutId);
      if (exists) return prev.filter((c) => c.optionId !== cutId);
      return [...prev, { optionId: cutId }];
    });
  }, []);

  const handlePrimalNoteChange = useCallback(
    (primalId: string, note: string) => {
      setPrimalNotes((prev) => ({ ...prev, [primalId]: note }));
    },
    [],
  );

  const handleAdvancedSpecValueChange = useCallback(
    (specId: string, value: string | string[]) => {
      setSpecifications((prev) => {
        const isEmpty = Array.isArray(value) ? value.length === 0 : !value;
        if (isEmpty) return prev.filter((s) => s.specification !== specId);
        const specDef = catalog.specifications.find((s) => s.id === specId);
        const entry: ResponseSpecificationSelection = {
          specification: specId,
          type: specDef?.inputType ?? 'text',
          value,
        };
        const idx = prev.findIndex((s) => s.specification === specId);
        if (idx === -1) return [...prev, entry];
        return prev.map((s, i) => (i === idx ? entry : s));
      });
    },
    [catalog.specifications],
  );

  const handleCutSpecValueChange = useCallback(
    (cutId: string, specId: string, value: string) => {
      setSelectedCuts((prev) => {
        const idx = prev.findIndex((c) => c.optionId === cutId);
        if (idx === -1) {
          // Cut not yet selected — add it now with this spec attached
          return [
            ...prev,
            {
              optionId: cutId,
              specifications: value
                ? [{ specification: specId, type: 'text' as const, value }]
                : undefined,
            },
          ];
        }
        const cut = prev[idx];
        const existingSpecs = cut.specifications ?? [];
        const specIdx = existingSpecs.findIndex(
          (s) => s.specification === specId,
        );
        let newSpecs: ResponseSpecificationSelection[];
        if (!value) {
          newSpecs = existingSpecs.filter((s) => s.specification !== specId);
        } else if (specIdx === -1) {
          newSpecs = [
            ...existingSpecs,
            { specification: specId, type: 'text' as const, value },
          ];
        } else {
          newSpecs = existingSpecs.map((s, i) =>
            i === specIdx ? { ...s, value } : s,
          );
        }
        const updated: ResponseCutOption = {
          ...cut,
          specifications: newSpecs.length > 0 ? newSpecs : undefined,
        };
        return prev.map((c, i) => (i === idx ? updated : c));
      });
    },
    [],
  );

  /**
   * Mode for the trim section: 'selection_by_percentage' when the questionnaire
   * has a trim_percentage question, otherwise 'ranked'. Controls how new trims
   * are shaped and how the advanced view renders totals.
   */
  const trimMode = useMemo<'ranked' | 'selection_by_percentage'>(() => {
    for (const q of questionnaire.questions) {
      if (q.type === 'trim_percentage') return 'selection_by_percentage';
      if (q.type === 'trim_ranking') return 'ranked';
    }
    return 'ranked';
  }, [questionnaire.questions]);

  const handleAddTrim = useCallback(
    (trimId: string) => {
      setTrimSelections((prev) => {
        const currentTrims = prev?.trim ?? [];

        if (trimMode === 'selection_by_percentage') {
          // Evenly redistribute percentages when adding — matches the
          // trim-percentage-wizard behaviour so the totals always sum to 100.
          const currentTotal = currentTrims.reduce(
            (sum, t) => sum + ('percentage' in t ? t.percentage : 0),
            0,
          );
          const totalItems = currentTrims.length + 1;
          const idealPercentage = 100 / totalItems;

          let newPercentage = idealPercentage;
          let adjustedExisting = currentTrims.map((t) => ({
            ...t,
            percentage: 'percentage' in t ? t.percentage : 0,
          }));

          if (currentTotal > 0) {
            const remainingForExisting = 100 - idealPercentage;
            adjustedExisting = adjustedExisting.map((t) => ({
              ...t,
              percentage: (t.percentage / currentTotal) * remainingForExisting,
            }));
            const newTotal = adjustedExisting.reduce(
              (sum, t) => sum + t.percentage,
              0,
            );
            newPercentage = 100 - newTotal;
          }

          // Round whole, correct rounding drift onto the first entry
          const rounded = [
            ...adjustedExisting.map((t) => ({
              ...t,
              percentage: Math.floor(t.percentage),
            })),
            { percentage: Math.floor(newPercentage) },
          ];
          const totalRounded = rounded.reduce(
            (sum, t) => sum + t.percentage,
            0,
          );
          const remainder = 100 - totalRounded;
          if (remainder > 0 && rounded.length > 0) {
            rounded[0].percentage += remainder;
          }

          const finalExisting: ResponsePercentageTrimSelection[] =
            adjustedExisting.map((t, i) => ({
              optionId: t.optionId,
              percentage: rounded[i].percentage,
              ...(t.specifications ? { specifications: t.specifications } : {}),
            }));
          const newTrim: ResponsePercentageTrimSelection = {
            optionId: trimId,
            percentage: rounded[rounded.length - 1].percentage,
          };
          return {
            type: 'selection_by_percentage',
            trim: [...finalExisting, newTrim],
          };
        }

        const newTrim: ResponseRankedTrimSelection = {
          optionId: trimId,
          rank: currentTrims.length + 1,
          quantity: 0,
        };
        return { type: 'ranked', trim: [...currentTrims, newTrim] };
      });
    },
    [trimMode],
  );

  const handleRemoveTrim = useCallback((index: number) => {
    setTrimSelections((prev) => {
      if (!prev) return prev;
      const remaining = prev.trim.filter((_, i) => i !== index);
      if (remaining.length === 0) return undefined;
      // Re-rank ranked entries after removal
      const reRanked = remaining.map((t, i) =>
        'rank' in t ? { ...t, rank: i + 1 } : t,
      );
      return { ...prev, trim: reRanked };
    });
  }, []);

  const handleMoveTrim = useCallback(
    (index: number, direction: 'up' | 'down') => {
      setTrimSelections((prev) => {
        if (!prev) return prev;
        const swapIdx = direction === 'up' ? index - 1 : index + 1;
        if (swapIdx < 0 || swapIdx >= prev.trim.length) return prev;
        const next = [...prev.trim];
        [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
        // Re-rank ranked entries after move
        const reRanked = next.map((t, i) =>
          'rank' in t ? { ...t, rank: i + 1 } : t,
        );
        return { ...prev, trim: reRanked };
      });
    },
    [],
  );

  const handleTrimSectionNoteChange = useCallback((note: string) => {
    setTrimSectionNotes(note);
  }, []);

  const handleTrimSpecValueChange = useCallback(
    (trimSelectionIndex: number, specId: string, value: string) => {
      setTrimSelections((prev) => {
        if (!prev || !prev.trim[trimSelectionIndex]) return prev;
        const trimSelection = prev.trim[trimSelectionIndex];
        const existingSpecs = trimSelection.specifications ?? [];
        const specIdx = existingSpecs.findIndex(
          (s) => s.specification === specId,
        );
        let newSpecs: ResponseSpecificationSelection[];
        if (!value) {
          newSpecs = existingSpecs.filter((s) => s.specification !== specId);
        } else if (specIdx === -1) {
          newSpecs = [
            ...existingSpecs,
            { specification: specId, type: 'text' as const, value },
          ];
        } else {
          newSpecs = existingSpecs.map((s, i) =>
            i === specIdx ? { ...s, value } : s,
          );
        }
        const updated = {
          ...trimSelection,
          specifications: newSpecs.length > 0 ? newSpecs : undefined,
        } as typeof trimSelection;
        return {
          ...prev,
          trim: prev.trim.map((t, i) =>
            i === trimSelectionIndex ? updated : t,
          ),
        };
      });
    },
    [],
  );

  const handleTrimWeightChange = useCallback(
    (index: number, weight: number) => {
      setTrimSelections((prev) => {
        if (!prev || !prev.trim[index]) return prev;
        const updated = { ...prev.trim[index], quantity: weight };
        return {
          ...prev,
          trim: prev.trim.map((t, i) => (i === index ? updated : t)),
        };
      });
    },
    [],
  );

  const handleTrimPercentageChange = useCallback(
    (index: number, percentage: number) => {
      setTrimSelections((prev) => {
        if (!prev || !prev.trim[index]) return prev;
        const clamped = Math.max(0, Math.min(100, percentage));
        const existing = prev.trim[index];
        const updated = {
          ...existing,
          percentage: clamped,
        } as typeof existing;
        return {
          ...prev,
          trim: prev.trim.map((t, i) => (i === index ? updated : t)),
        };
      });
    },
    [],
  );

  // ── Response draft ──────────────────────────────────────────────────────────
  // Direct pass-through of canonical state — no transformation needed.

  const responseDraft = useMemo(
    (): CutsheetResponseDraft => ({
      questionnaireId: questionnaire.id,
      name: cutsheetName || undefined,
      // Map the UI save-type to the draft's narrower type union.
      // 'producer' and 'buyer' are user-scoped cutsheets on the API.
      type:
        fixedCutsheetType === 'vendor' || cutsheetSaveType === 'vendor'
          ? 'vendor'
          : 'user',
      selectedCuts,
      trimSelections,
      specifications: specifications.length > 0 ? specifications : undefined,
    }),
    [
      questionnaire.id,
      cutsheetName,
      cutsheetSaveType,
      fixedCutsheetType,
      selectedCuts,
      trimSelections,
      specifications,
    ],
  );

  // ── Derived navigation ──────────────────────────────────────────────────────

  const currentQuestion = questionnaire.questions[currentStep];
  const currentQuestionError = currentQuestion
    ? (questionErrors[currentQuestion.id] ?? '')
    : '';
  const isFirstStep = currentStep === 0;
  const isLastQuestionStep = currentStep === questionnaire.questions.length - 1;
  const isReviewStep = currentStep === questionnaire.questions.length;
  const isSaveStep = currentStep === questionnaire.questions.length + 1;

  // ── Provide ─────────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!cutsheetName.trim()) {
      throw new Error('Please enter a name for your cutsheet before saving.');
    }
    const effectiveSaveType = fixedCutsheetType ?? cutsheetSaveType;
    // Derive the userId to save against based on the save type.
    // fixedUserId wins when provided (e.g. legacy-cutsheet conversion needs to
    // retain the original buyer's ownership instead of reassigning to req.user).
    const userId =
      fixedUserId ??
      (effectiveSaveType === 'producer'
        ? producerId
        : effectiveSaveType === 'buyer'
          ? buyerId
          : undefined);
    const result = await upsertResponse({
      variables: {
        questionnaireId: questionnaire.id,
        name: cutsheetName.trim(),
        type:
          effectiveSaveType === 'vendor'
            ? 'vendor'
            : effectiveSaveType === 'orphan'
              ? 'orphan'
              : 'user',
        userId,
        selectedCuts: selectedCuts.map((c) => ({
          optionId: c.optionId,
          specifications: c.specifications?.map((s) => ({
            specification: s.specification,
            type: s.type,
            value: String(s.value),
          })),
        })),
        trimSelections: trimSelections
          ? {
              type: trimSelections.type,
              trim: trimSelections.trim.map((t) => ({
                optionId: t.optionId,
                ...('rank' in t
                  ? { rank: t.rank, quantity: (t as any).quantity ?? 0 }
                  : { percentage: (t as any).percentage ?? 0 }),
                specifications: t.specifications?.map((s) => ({
                  specification: s.specification,
                  type: s.type,
                  value: String(s.value),
                })),
              })),
            }
          : undefined,
        specifications:
          specifications.length > 0
            ? specifications.map((s) => ({
                specification: s.specification,
                type: s.type,
                value: String(s.value),
              }))
            : undefined,
      },
    });
    const savedId = result.data?.cutsheetQuestionnaireResponseUpsert?._id;
    if (savedId) {
      onSave?.(savedId);
    }
    handleClose();
  }, [
    buyerId,
    cutsheetName,
    cutsheetSaveType,
    fixedCutsheetType,
    fixedUserId,
    handleClose,
    onSave,
    producerId,
    questionnaire.id,
    selectedCuts,
    specifications,
    trimSelections,
    upsertResponse,
  ]);

  const handleClearSubSelections = useCallback((questionId: string) => {
    setSubSelections((prev) => {
      const cutIdsToRemove = new Set(prev[questionId] ?? []);
      if (cutIdsToRemove.size > 0) {
        // Remove those cuts from canonical state so nothing lingers
        setSelectedCuts((prevCuts) =>
          prevCuts.filter((c) => !cutIdsToRemove.has(c.optionId)),
        );
      }
      return { ...prev, [questionId]: [] };
    });
  }, []);

  const value: QuestionnaireResponseContextValue = {
    questionnaire,
    catalog,
    currentStep,
    isFirstStep,
    isLastQuestionStep,
    isReviewStep,
    isSaveStep,
    hasAdvancedStep,
    hasVisitedReview,
    heroExpanded,
    setHeroExpanded,
    currentQuestion,
    questionnaireCutIds,
    handleNext,
    handleBack,
    handleGoToReview,
    handleGoToSave,
    handleNavigateToQuestion,
    handleClose,
    isSaving,
    handleSave,
    answers,
    itemSpecValues,
    subSelections,
    handleAnswerChange,
    handleSpecChange,
    handleSubSelectionToggle,
    handleClearSubSelections,
    selectedCuts,
    specifications,
    trimSelections,
    primalNotes,
    trimSectionNotes,
    selectedCutIds,
    cutSpecValues,
    advancedSpecValues,
    selectedTrimIds,
    handleToggleCut,
    handlePrimalNoteChange,
    handleAdvancedSpecValueChange,
    handleCutSpecValueChange,
    trimMode,
    handleAddTrim,
    handleRemoveTrim,
    handleMoveTrim,
    handleTrimSectionNoteChange,
    handleTrimSpecValueChange,
    handleTrimWeightChange,
    handleTrimPercentageChange,
    questionErrors,
    currentQuestionError,
    responseDraft,
    cutsheetName,
    cutsheetSaveType,
    isProcessor,
    producerId,
    buyerId,
    fixedCutsheetType,
    fixedUserId,
    handleCutsheetNameChange,
    handleCutsheetSaveTypeChange,
  };

  return (
    <QuestionnaireResponseContext.Provider value={value}>
      {children}
    </QuestionnaireResponseContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Access the questionnaire response context.
 * Must be used inside a `QuestionnaireResponseProvider`.
 */
export const useQuestionnaireResponse =
  (): QuestionnaireResponseContextValue => {
    const ctx = useContext(QuestionnaireResponseContext);
    if (!ctx) {
      throw new Error(
        'useQuestionnaireResponse must be used inside a QuestionnaireResponseProvider',
      );
    }
    return ctx;
  };
